import React, { useEffect, useState } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDChartLegend,
  FDPaper,
  FDStack,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { ProgressBar } from 'Views/FreemiumEntry/HasRelationship/Content/Templates/PicklistQuestion/ProgressBar';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { DataSetCollection } from 'Organisms/DataSet';
import StepNavigationFooter from 'Packages/FacetDesign/Components/Experimental/StepNavigationFooter';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import { LottieLoadingLayout } from 'Organisms/LottieControl/LottieLoadingLayout';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';
import {
  useBuildCommonFQEventProperties,
} from 'Views/FreemiumEntry/HasRelationship/Content/Components/utils';
import { QuizWrapperLayout } from 'Views/FreemiumEntry/HasRelationship/Content/Components/QuizWrapperLayout';
import { importRecharts } from 'Helpers/External';
import { InsightLoadingLottie } from './InsightLoadingLottie';

const useStyles = buildUseStyles('InsightAnalysis', () => ({
  chartContainer: {
    marginLeft: '-30px',
    marginRight: '-10px',
  },
}));

const InsightAnalysis = () => {
  const classes = useStyles();
  const isMobile = useMobileLayout();
  const { resource, onSubmit, saving } = useMetaLayoutContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { buildCommonFQEventProperties } = useBuildCommonFQEventProperties();
  const [loading, setLoading] = useState();

  // Load recharts asynchronously
  const rechartsLoadedInitialState = !!window.Recharts;
  const [rechartsLoaded, setRechartsLoaded] = useState(rechartsLoadedInitialState);
  useEffect(() => {
    if (!rechartsLoadedInitialState) {
      importRecharts().then(() => setRechartsLoaded(true));
    }
  }, []);

  useScrollTopOnMount();
  useBoneBackgroundColor();

  const onNext = () => {
    logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
      eventProperties: {
        ...buildCommonFQEventProperties(resource),
        insightId: resource.attr('insightId'),
        insightReferenceName: resource.attr('insightReferenceName'),
      },
    });
    onSubmit();
  };

  return (
    <QuizWrapperLayout>
      <FDStack direction={'column'} spacing={4} {...dataAttrs('insight-container')}>
        <ProgressBar />
        <MetaControlField attr={'insightTitle'} />
        <MetaControlField attr={'insightDescription'} />
        <LottieLoadingLayout>
          {resource.withRelationship(
            fr.relationships.loadingLottie.name,
            (rel) => (<InsightLoadingLottie
              onNext={onNext}
              relationship={rel}
              lottieDuration={8000}
              setLoading={setLoading}
            />),
            () => null,
          )}
          {rechartsLoaded && resource.attr('dataSet') && !resource.attr('dataSet')?.resources().empty() ? (
            <FDPaper appearance={'elevated'} padding={4}>
              <FDStack direction={'column'} spacing={2}>
                <div className={classes.chartContainer} {...dataAttrs('fq-chart-container')}>
                  <DataSetCollection collection={resource.attr('dataSet')} dataSetHeight={isMobile ? 300 : 400} ChartLegend={FDChartLegend} showAxisLine />
                </div>
              </FDStack>
            </FDPaper>) : null}
        </LottieLoadingLayout>
        {resource.meta().canPut() && <StepNavigationFooter
          onNext={onNext}
          disabled={loading}
          isLoading={saving}
          label={resource.attr('primaryActionLabel') || 'Continue'}
        />}
      </FDStack>
    </QuizWrapperLayout>
  );
};

export { InsightAnalysis };
